import { useQuery } from '@tanstack/react-query';

import {
    getOverrideChannelFields,
    isOverrideChannelStructure,
} from '../../helpers/channelDefinitionHelpers';
import { overrideFormulaFieldsWithMessageContent } from '../../helpers/formulaHelpers';
import { BaseError } from 'helpers/BaseError';

import { ProcessedFormulaV1 } from '../../models/formula.model';

import API from '../../services/api';

import useGetAndUpdateMessageContent from '../message-content/useMessageContentGetAndUpdate';
import formulaKeys from './_queryKeys';

const getFormula = async (
    formulaId: number,
    messageContentId: string,
): Promise<ProcessedFormulaV1> => {
    const { data } = await API.fetchApprovedFormulaV1(formulaId, {
        message_content_id: messageContentId,
    });
    return data;
};

// Many consumers of this formula (alternates, placeholder-character
// helpers, ...) read `structure` assuming it's always a Slate
// Descendant[] array. On an override channel it's a raw v2 dict instead -
// normalize it to a safe empty array here, once, and expose the raw
// fields separately for the schema-driven read-only view to consume.
const sanitizeOverrideChannelStructure = (
    formula: ProcessedFormulaV1,
): ProcessedFormulaV1 => {
    const rawStructure = formula.structure;
    if (!isOverrideChannelStructure(rawStructure)) {
        return formula;
    }
    return {
        ...formula,
        structure: [],
        override_channel_fields: getOverrideChannelFields(rawStructure),
    };
};

export default function useApprovedFormulaQueryV1(formulaId: number) {
    const { data: messageContent, isDataLoading: isMessageContentLoading } =
        useGetAndUpdateMessageContent(formulaId);

    const queryResult = useQuery<ProcessedFormulaV1, BaseError>({
        queryKey: formulaKeys.approvedFormulaById(
            formulaId,
            messageContent?.id,
        ),
        queryFn: () => getFormula(formulaId, messageContent?.id as string),
        enabled: !!formulaId && !!messageContent?.id,
        select: (data) =>
            sanitizeOverrideChannelStructure(
                overrideFormulaFieldsWithMessageContent(data, messageContent),
            ),
    });

    const isLoading = isMessageContentLoading || queryResult.isLoading;

    return {
        ...queryResult,
        isLoading,
    };
}
